import './AttributesMappingTable.scss';
import React, { useCallback, useMemo, useState } from 'react';
import { FlightButton, FlightTable } from '@flybits/design-system';
import { ReactComponent as MappingEmpty } from 'assets/images/mapping-empty.svg';
import { ReactComponent as SyncTemplateBannerImage } from 'assets/images/sync-template-banner-image.svg';
import { ReactComponent as PluginsUnavailable } from 'assets/images/warning.svg';
import { ReactComponent as PlaceholdersEmpty } from 'assets/images/tags.svg';

import MappingTag from './MappingTag/MappingTag';
import MappedAttribute from './MappedAttribute/MappedAttribute';
import StatusBadge, { STATUS_TYPES } from 'components/Shared/StatusBadge/StatusBadge';
import AttributeMappingModal from '../AttributeMappingModal/AttributeMappingModal';
import type { TAttributeParameter, TemplatePayloadData } from 'components/FlowBuilder/FlowSidePanels/types';
import { Info } from '@material-ui/icons';
import useConfirmModal from 'hooks/useConfirmModal';
import type { ConfirmationDialogProps } from 'components/Shared/shared.types';
import { TMappedPlugin } from 'hooks/useContextPlugins';
import { getFormattedContextAttributeDisplay } from 'pages/FlowBuilder/helpers/flow-builder.helpers';

const MAIN_CLASS = 'attributes-mapping-table';
const CLASSES = {
  CONTAINER: `${MAIN_CLASS}__container`,
  HEADER: `${MAIN_CLASS}__container__header`,
  HEADER_TITLE: `${MAIN_CLASS}__container__header__title`,
  HEADER_TITLE_RED: `${MAIN_CLASS}__container__header__title--red`,
  INFO: `${MAIN_CLASS}__container__info`,
  INFO_ICON: `${MAIN_CLASS}__container__info__icon`,
  INFO_CONTENT: `${MAIN_CLASS}__container__info__content`,
  INFO_TEXT: `${MAIN_CLASS}__container__info__text`,
  INFO_SUBTEXT: `${MAIN_CLASS}__container__info__subtext`,
  INFO_BUTTON_CONTAINER: `${MAIN_CLASS}__container__info__button__container`,
  TABLE: `${MAIN_CLASS}__container__table`,
  EMPTY: `${MAIN_CLASS}__container__empty`,
};
const STATUS_TITLES = {
  [STATUS_TYPES.STATUS_AUTO_MAPPED]: 'Auto-mapped',
  [STATUS_TYPES.STATUS_NEEDS_MAPPING]: 'Needs Mapping',
  [STATUS_TYPES.STATUS_MANUAL_EDIT]: 'Manual-edit',
};

export type AttributeErrorProps = {
  errorMessage: string;
  errorType: 'warning' | 'error' | null;
};

interface AttributesMappingTableProps {
  type: 'email' | 'sms';
  defaultTemplatePayloadData?: TemplatePayloadData | null;
  providerTemplatePayloadData?: TemplatePayloadData | null;
  payloadDataErrors?: Record<string, AttributeErrorProps>;
  isLoading?: boolean;
  onChange?: (payload: TemplatePayloadData) => void;
  onConfirmSyncTemplate?: () => void;
  mappedAttributes?: TMappedPlugin[] | null;
  isTemplateError?: boolean;
}

// Define the type for table data
interface AttributeMappingTableRow {
  key: string;
  tag: React.ReactNode;
  status: React.ReactNode;
  mapped_to: React.ReactNode;
}

/**
 * Renders the AttributesMappingTable component, which displays a table for mapping tags from an email or SMS template
 * (such as SendGrid or Twilio) to Flybits context plugins. The table shows the tags present in the template, their mapping
 * status, and the corresponding context plugin they are mapped to. Users can review and override auto-mapped attributes.
 *
 * @param {AttributesMappingTableProps} props - The props for the component.
 * @param {'email' | 'sms'} props.type - The type of template being mapped (either 'email' or 'sms').
 * @param {TemplatePayloadData | null} props.defaultTemplatePayloadData - The default email template payload data for reference.
 * @param {TemplatePayloadData | null} props.typeTemplatePayloadData - The email template payload data containing attribute mappings.
 * @param {boolean} [props.isLoading=false] - Optional flag indicating whether the table is in a loading state.
 * @param {(payload: TemplatePayloadData) => void} [props.onChange] - Optional callback invoked when the template mapping is updated.
 * @param {() => void} [props.onConfirmSyncTemplate] - Optional callback invoked when the user confirms syncing the template.
 *
 * @returns {JSX.Element} The rendered AttributesMappingTable component.
 */
function AttributesMappingTable({
  type,
  defaultTemplatePayloadData,
  providerTemplatePayloadData,
  isLoading,
  onChange,
  onConfirmSyncTemplate,
  mappedAttributes,
  payloadDataErrors,
  isTemplateError,
}: AttributesMappingTableProps): JSX.Element {
  const hasNoPlugins = !mappedAttributes?.length;
  const providerTemplatePayloadDataHasKeys =
    (providerTemplatePayloadData && Object.keys(providerTemplatePayloadData).length > 0) ||
    (defaultTemplatePayloadData && Object.keys(defaultTemplatePayloadData).length > 0);

  // leaving the 'no-template-selected' empty state out of this logic as the designs are unclear about it for now
  const tableError: ErrorType | '' = hasNoPlugins
    ? 'plugins-unavailable'
    : !providerTemplatePayloadDataHasKeys
    ? 'no-placeholders'
    : '';
  const [showAttributeModal, setShowAttributeModal] = useState(false);
  const [selectedTagName, setSelectedTagName] = useState('');
  const [selectedAttributeId, setSelectedAttributeId] = useState('');
  const [selectedAttributeParameters, setSelectedAttributeParameters] = useState<TAttributeParameter[]>([]);
  const [selectedAttributeMapKey, setSelectedAttributeMapKey] = useState<string>('');
  const [selectedAttributeDefaultValue, setSelectedAttributeDefaultValue] = useState<string>('');

  const syncConfirmationDialogProps: ConfirmationDialogProps = {
    icon: <SyncTemplateBannerImage />,
    title: 'Are you sure you want to sync?',
    description: `Syncing may impact changes based on template updates.`,
    primaryAction: {
      value: 'Yes, continue',
      onClickHandler: onConfirmSyncTemplate,
    },
    secondaryAction: {
      value: 'Cancel',
    },
  };
  const [SyncTemplateConfirmModal, showSyncTemplateConfirmModal] = useConfirmModal(syncConfirmationDialogProps);

  const handleOnSyncClicked = useCallback(async () => {
    try {
      showSyncTemplateConfirmModal();
    } catch {}
  }, [showSyncTemplateConfirmModal]);

  const tableHeaders = [
    {
      name: `${type === 'email' ? 'Placeholders in the Email' : 'Placeholders in the SMS'}`,
      key: 'tag',
      isVisible: true,
      hideTooltip: false,
      isSortable: false,
      tooltip: `${
        type === 'email'
          ? 'The tags defined in the SendGrid template for this email.'
          : 'The tags defined in the Twilio template for this SMS message.'
      }`,
    },
    {
      name: 'Status',
      key: 'status',
      isVisible: true,
      hideTooltip: false,
      isSortable: false,
      tooltip: `${
        type === 'email'
          ? 'Mapping status of tags in SendGrid email template to Flybits context plugins.'
          : 'The mapping status of the tags defined in the Twilio SMS template to the Flybits context plugins.'
      }`,
    },
    {
      name: 'Mapped to',
      key: 'mapped_to',
      isVisible: true,
      hideTooltip: false,
      isSortable: false,
      tooltip: `${
        type === 'email'
          ? 'The context plugin that is mapped to the tags defined in the SendGrid email template.'
          : 'The context plugin that is mapped to the tags defined in the Twilio SMS template.'
      }`,
    },
  ];

  const handleEditAttribute = useCallback(() => {
    setShowAttributeModal(true);
  }, []);

  const mappingTableData: AttributeMappingTableRow[] = useMemo(
    () =>
      providerTemplatePayloadData
        ? Object.entries({ ...(defaultTemplatePayloadData ?? {}), ...providerTemplatePayloadData }).map(
            ([key, value]) => {
              // Derive the attributeId by removing the surrounding {{ }}
              const { attribute, attributeParameters, attributeDefaultValue, attributeMapKey } = value;
              const attributeId = attribute
                ? attribute.startsWith('{{') && attribute.endsWith('}}')
                  ? attribute.slice(2, -2)
                  : attribute
                : '';
              // TODO: compare attributeParameters when they are supported
              const { attribute: defaultAttribute } = defaultTemplatePayloadData?.[key] || {};
              const defaultAttributeId = defaultAttribute
                ? defaultAttribute.startsWith('{{') && defaultAttribute.endsWith('}}')
                  ? defaultAttribute.slice(2, -2)
                  : defaultAttribute
                : '';
              let status = STATUS_TYPES.STATUS_MANUAL_EDIT;
              if (attributeId === defaultAttributeId) {
                status = attributeId ? STATUS_TYPES.STATUS_AUTO_MAPPED : STATUS_TYPES.STATUS_NEEDS_MAPPING;
              }

              const mappedValue = mappedAttributes?.find(
                (plugin) => plugin.attribute.uid === attribute.match(/{{(.*?)}}/)?.[1],
              );

              const mappedParameters: TAttributeParameter[] =
                attributeParameters?.map((parameter, idx) => {
                  const mappedParameter = mappedValue?.attribute.parameters?.[idx];
                  return {
                    ...parameter,
                    name: mappedParameter?.name || mappedParameter?.key || parameter.name,
                  };
                }) || [];

              // Check for errors
              const errorProps = payloadDataErrors?.[key] ?? null;

              return {
                key,
                tag: <MappingTag tag={`{{${key}}}`} />,
                status: <StatusBadge status={status} title={STATUS_TITLES[status]} />,
                mapped_to: (
                  <MappedAttribute
                    value={
                      mappedValue
                        ? getFormattedContextAttributeDisplay(mappedValue.name, mappedParameters)
                        : attributeId
                    }
                    onAdd={() => {
                      setSelectedTagName(key);
                      setSelectedAttributeId(attributeId);
                      handleEditAttribute();
                    }}
                    onEdit={
                      attribute?.startsWith('{{') && attribute?.endsWith('}}')
                        ? () => {
                            setSelectedTagName(key);
                            setSelectedAttributeId(attributeId);
                            setSelectedAttributeParameters(mappedParameters);
                            setSelectedAttributeDefaultValue(attributeDefaultValue || '');
                            setSelectedAttributeMapKey(attributeMapKey || '');
                            handleEditAttribute();
                          }
                        : null
                    }
                    isError={!!errorProps}
                    {...errorProps}
                  />
                ),
              };
            },
          )
        : [],
    [providerTemplatePayloadData, defaultTemplatePayloadData, handleEditAttribute, mappedAttributes, payloadDataErrors],
  );

  const handleApplyAttributeMapping = (
    tagName: string,
    attributeId: string,
    parameters: TAttributeParameter[],
    defaultValue: string,
    selectedKey?: string,
  ) => {
    if (providerTemplatePayloadData) {
      const newTemplatePayloadData: TemplatePayloadData = {
        ...providerTemplatePayloadData,
        [tagName]: {
          attribute: `{{${attributeId}}}`,
          attributeParameters: parameters,
          attributeDefaultValue: defaultValue,
          attributeMapKey: selectedKey,
        },
      };

      onChange?.(newTemplatePayloadData);
      onClose();
    }
  };

  const onClose = useCallback(() => {
    setSelectedTagName('');
    setSelectedAttributeId('');
    setSelectedAttributeParameters([]);
    setShowAttributeModal(false);
  }, []);

  return (
    <div className={MAIN_CLASS}>
      <div className={CLASSES.CONTAINER}>
        {isTemplateError ? (
          <ErrorArea errorType="template-error" type={type} />
        ) : providerTemplatePayloadData || defaultTemplatePayloadData ? (
          <>
            <div className={CLASSES.HEADER}>
              <div className={CLASSES.HEADER_TITLE}>
                Mapping attributes<span className={CLASSES.HEADER_TITLE_RED}>*</span>
              </div>
            </div>
            <div className={CLASSES.INFO}>
              <div className={CLASSES.INFO_ICON}>
                <Info />
              </div>
              <div className={CLASSES.INFO_CONTENT}>
                <div className={CLASSES.INFO_TEXT}>
                  Made changes to a {type === 'email' ? 'SendGrid' : 'Twilio'} template?
                </div>
                <div className={CLASSES.INFO_SUBTEXT}>
                  Always use the latest version of your {type === 'email' ? 'SendGrid' : 'Twilio'} template. If you
                  updated it, click the <b>sync now</b> button.
                </div>
              </div>
              <div className={CLASSES.INFO_BUTTON_CONTAINER}>
                <FlightButton label="Sync now" size="medium" theme="secondary" onClick={handleOnSyncClicked} />
              </div>
            </div>

            {tableError ? (
              <ErrorArea errorType={tableError} />
            ) : (
              <div className={CLASSES.TABLE}>
                <FlightTable
                  tableHeaders={tableHeaders}
                  hasPaginationBeforeTable={false}
                  hasPaginationAfterTable={false}
                  isLoading={isLoading}
                  tableData={mappingTableData}
                />
              </div>
            )}
          </>
        ) : (
          <ErrorArea errorType="no-template-selected" />
        )}
      </div>
      <AttributeMappingModal
        isVisible={showAttributeModal}
        onClose={onClose}
        onApply={handleApplyAttributeMapping}
        tagName={selectedTagName}
        attributeId={selectedAttributeId}
        attributeMapKey={selectedAttributeMapKey}
        attributeParameters={selectedAttributeParameters}
        attributeDefaultValue={selectedAttributeDefaultValue}
      />
      <SyncTemplateConfirmModal />
    </div>
  );
}

type ErrorType = 'no-placeholders' | 'plugins-unavailable' | 'no-template-selected' | 'template-error';
const ErrorMessageContents: Record<ErrorType, { title: string; message: string; icon: React.JSX.Element }> = {
  'no-placeholders': {
    icon: <PlaceholdersEmpty />,
    title: 'No placeholders found in template',
    message: 'The selected template does not contain any placeholders to map.',
  },
  'plugins-unavailable': {
    icon: <PluginsUnavailable />,
    title: 'Unable to load plugin list',
    message: 'Please reload the page or reach out to support to solve this issue.',
  },
  'template-error': {
    icon: <PluginsUnavailable />,
    title: '',
    message: 'Please refresh the page or try again later.',
  },
  'no-template-selected': {
    icon: <MappingEmpty />,
    title: 'Preview details',
    message: 'Select a template to start seeing details about it',
  },
};
function ErrorArea({ errorType, type }: { errorType: ErrorType; type?: 'email' | 'sms' }) {
  const { icon, message } = ErrorMessageContents[errorType];
  let title = ErrorMessageContents[errorType].title;
  if (errorType === 'template-error') {
    title = type === 'sms' ? 'Unable to load the selected sms template' : 'Unable to load the selected email template';
  }
  return (
    <div className={CLASSES.EMPTY}>
      {icon}
      <h3>{title}</h3>
      <p>{message}</p>
    </div>
  );
}

export default AttributesMappingTable;
